import { useState } from 'react';
import { isPublishedVideo, type DemoVideo as DemoVideoMeta } from '@transport/product-metadata';
import { track } from './analytics';
import { demoVideoEmbedUrl } from './demoVideoUrl';

/**
 * A demo video behind a click-to-load poster. Until the visitor presses play the page holds only a
 * local screenshot and a button — no YouTube script, cookie or request — and then the
 * youtube-nocookie player replaces it. Renders nothing while the cut has no published id
 * (`demoVideo.ts` in product metadata).
 */
export function DemoVideo({
  video,
  placement,
  poster = 'dashboard',
}: {
  video: DemoVideoMeta;
  /** Where on the site the player sits, for the analytics event. */
  placement: string;
  /** A product screenshot slug (`public/product/<slug>-960.webp`) shown until play. */
  poster?: string;
}) {
  const [playing, setPlaying] = useState(false);
  if (!isPublishedVideo(video)) return null;
  const headingId = `demo-video-${placement}`;

  return (
    <section className="section compact demo-video-section" aria-labelledby={headingId}>
      <div className="container demo-video">
        <div className="demo-video-copy" lang={video.language}>
          <h2 id={headingId}>{video.title}</h2>
          <p>{video.description}</p>
        </div>
        <div className="demo-video-frame">
          {playing ? (
            <iframe
              src={demoVideoEmbedUrl(video.youtubeId)}
              title={video.title}
              loading="lazy"
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
          ) : (
            <button
              type="button"
              className="demo-video-poster"
              aria-label={`${video.title} — play video`}
              onClick={() => {
                setPlaying(true);
                track('demo_video_play', { placement, language: video.language });
              }}
            >
              <img
                src={`/product/${poster}-960.webp`}
                alt=""
                width={960}
                height={540}
                loading="lazy"
                decoding="async"
              />
              <span className="demo-video-play" aria-hidden="true">
                ▶
              </span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
